/**
 * Live-update version watcher.
 *
 * Every production build bakes a unique __BUILD_ID__ into the bundle and emits
 * the same id to /version.json (vite.config.ts). This module polls
 * version.json and compares — a mismatch means a newer deploy is live and this
 * tab is running stale code ("out of sync").
 *
 * Applying the update is deliberately gentle so we never destroy in-progress
 * work (Capture Deal is a huge form):
 *   1. On the user's NEXT route navigation the update is applied via a full
 *      reload (the page was changing anyway — nothing is lost). See UpdateGate.
 *   2. If the user has been idle for a while (no typing/clicking and no
 *      focused input), the update applies automatically with the overlay.
 *   3. A small "New version available" pill (UpdateGate) lets them apply
 *      immediately by choice.
 *
 * This prevents the reactive case (stale chunk → error) from ever happening
 * for most users: they're upgraded before they hit a missing chunk.
 */

import { showLiveUpdateOverlay } from './liveUpdateOverlay';

export const UPDATE_PENDING_EVENT = 'app:update-pending';

const POLL_INTERVAL_MS = 4 * 60_000;      // periodic check
const FOCUS_CHECK_MIN_GAP_MS = 60_000;    // throttle focus/visibility re-checks
const IDLE_AUTO_APPLY_MS = 10 * 60_000;   // idle this long → safe to auto-apply
const OVERLAY_DWELL_MS = 900;

let updatePending = false;
let lastCheckAt = 0;
let lastInteractionAt = Date.now();
let applying = false;

export function isUpdatePending(): boolean {
  return updatePending;
}

/** Apply the pending update now: overlay, then a full reload. */
export function applyUpdateNow(): void {
  if (applying) return;
  applying = true;
  showLiveUpdateOverlay();
  window.setTimeout(() => window.location.reload(), OVERLAY_DWELL_MS);
}

function markPending(): void {
  if (updatePending) return;
  updatePending = true;
  window.dispatchEvent(new CustomEvent(UPDATE_PENDING_EVENT));
}

function hasFocusedFormField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable === true;
}

function maybeAutoApplyWhenIdle(): void {
  if (!updatePending || applying) return;
  const idleMs = Date.now() - lastInteractionAt;
  if (idleMs >= IDLE_AUTO_APPLY_MS && !hasFocusedFormField()) {
    applyUpdateNow();
  }
}

async function checkVersion(): Promise<void> {
  lastCheckAt = Date.now();
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    // Older deploys (or the dev server) have no version.json — the SPA rewrite
    // returns index.html, which fails to parse as JSON. Treat as "no signal".
    const data = (await res.json()) as { buildId?: string };
    if (data?.buildId && data.buildId !== __BUILD_ID__) {
      markPending();
    }
  } catch {
    // Offline / transient / non-JSON — ignore and try again next cycle.
  }
}

/**
 * Start watching for new deploys. Call once at bootstrap. No-op on the dev
 * server (there is no version.json and HMR handles freshness).
 */
export function startVersionWatch(): void {
  if (import.meta.env.DEV) return;
  if (typeof __BUILD_ID__ === 'undefined') return;

  // Track user activity so idle auto-apply can't interrupt active work.
  const touch = () => { lastInteractionAt = Date.now(); };
  window.addEventListener('pointerdown', touch, { passive: true });
  window.addEventListener('keydown', touch, { passive: true });

  // Periodic poll (also opportunistically auto-applies when idle).
  window.setInterval(() => {
    checkVersion();
    maybeAutoApplyWhenIdle();
  }, POLL_INTERVAL_MS);

  // Re-check when the tab comes back into view / focus — the classic
  // "left the tab open overnight" case — throttled to avoid spam.
  const onReturn = () => {
    if (document.visibilityState !== 'visible') return;
    maybeAutoApplyWhenIdle();
    if (Date.now() - lastCheckAt >= FOCUS_CHECK_MIN_GAP_MS) {
      checkVersion();
    }
  };
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('focus', onReturn);

  // First check shortly after load (don't compete with app bootstrap).
  window.setTimeout(checkVersion, 15_000);
}
